import {ExitIntentPopupForm} from '@/components/exitIntentPopup/exitIntentPopupForm';
import {usePopupContext} from '@/components/PopupProvider';
import {useExitIntent} from '@/hooks/useExitIntent';
import {useIsMobile} from '@/hooks/useIsMobile';
import {popupState} from '@/lib/popupState';
import {submitWaitlist} from '@/server/waitlist';
import {X} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {useCallback, useEffect, useRef, useState} from 'react';

const POPUP_ID = 'exit-intent';
const AUTO_CLOSE_MS = 2000;

/**
 * Exit intent email capture popup (desktop only).
 * Fires when the cursor leaves the page (any mouseleave from the document),
 * targeting visitors who have not yet seen Popup 1 (scroll) or Popup 2
 * (behavioral). Lowest priority in the conflict resolution order — the
 * popup manager blocks it automatically if any other popup has already been
 * shown.
 */
function ExitIntentPopup() {
  const {activePopup, requestPopup, dismissPopup, convertPopup, isBlocked} =
    usePopupContext();
  const isMobile = useIsMobile();

  const {isTriggered} = useExitIntent({
    triggerOnce: false,
    enabled: !isMobile && !isBlocked && !popupState.hasBeenShown(POPUP_ID),
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isOpen = activePopup === POPUP_ID;

  // Clear the auto-close timer if the popup is dismissed before it fires.
  useEffect(() => {
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isTriggered && !isOpen) {
      const granted = requestPopup(POPUP_ID);
      if (granted) {
        popupState.recordShown(POPUP_ID);
      }
    }
  }, [isTriggered, isOpen, requestPopup]);

  const handleDismiss = useCallback(() => {
    dismissPopup();
  }, [dismissPopup]);

  const handleSubmit = useCallback(
    async (email: string) => {
      setIsSubmitting(true);
      setError(null);

      try {
        const response = await submitWaitlist({
          data: {
            email,
            source: 'exit-intent-popup',
          },
        });

        if (response.success) {
          setIsSuccess(true);
          autoCloseTimerRef.current = setTimeout(() => {
            convertPopup();
          }, AUTO_CLOSE_MS);
        } else {
          setError(response.error);
        }
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Please try again.',
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [convertPopup],
  );

  // Desktop only
  if (isMobile) return null;

  const headline = 'Your next race is out there.';
  const subhead =
    "We'll send you new races near you every Monday. Takes 5 seconds to sign up.";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.3}}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDismiss}
          />

          <motion.div
            initial={{opacity: 0, scale: 0.9, y: 20}}
            animate={{opacity: 1, scale: 1, y: 0}}
            exit={{opacity: 0, scale: 0.9, y: 20}}
            transition={{duration: 0.3, ease: 'easeOut'}}
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[450px] p-8 border border-slate-200"
          >
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              aria-label="Close popup"
              data-mp-element-name="exit-intent-popup-close"
              data-mp-cta-location="exit-intent-popup"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <h2 className="font-heading font-sans text-2xl text-slate-900 mb-2">
                {headline}
              </h2>
              <p className="text-slate-600">{subhead}</p>
            </div>

            <ExitIntentPopupForm
              onSubmit={handleSubmit}
              onDismiss={handleDismiss}
              isSubmitting={isSubmitting}
              error={error}
              isSuccess={isSuccess}
            />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export {ExitIntentPopup};
